import React, { useState, useEffect } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Switch } from "@/shared/components/ui/switch";
import { Textarea } from "@/shared/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { ArrowLeft, Save, User, Key, Shield, Copy, Eye, EyeOff, CheckCircle2, Zap, Lock, RefreshCw } from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import FeeCommissionSection from "@/shared/components/forms/FeeCommissionSection";

const providers = [
  { code: "SCB", name: "Siam Commercial Bank" },
  { code: "KBANK", name: "Kasikorn Bank" },
  { code: "BBL", name: "Bangkok Bank" },
  { code: "KTB", name: "Krungthai Bank" },
  { code: "TRUEWALLET", name: "TrueMoney Wallet" },
  { code: "PROMPTPAY", name: "PromptPay QR" },
  { code: "LINEPAY", name: "LINE Pay" },
  { code: "SHOPEEPAY", name: "ShopeePay" }, 
];

function generateApiKey(prefix = "eng_pk_") {
  return prefix + Array.from({ length: 32 }, () =>
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]
  ).join("");
}

function generatePassword() {
  const length = 10;
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const digits = "0123456789";
  const special = "!@#$%^&*()_+";
  const all = upper + lower + digits + special;

  let password = "";
  password += upper.charAt(Math.floor(Math.random() * upper.length));
  password += lower.charAt(Math.floor(Math.random() * lower.length));
  password += digits.charAt(Math.floor(Math.random() * digits.length));
  password += special.charAt(Math.floor(Math.random() * special.length));

  for (let i = password.length; i < length; i++) {
    password += all.charAt(Math.floor(Math.random() * all.length));
  }

  // Shuffle
  return password.split('').sort(() => 0.5 - Math.random()).join('');
}

export default function AdminEngineForm() {
  const { t } = useLanguage();
  const urlParams = new URLSearchParams(window.location.search);
  const engineId = urlParams.get("id");
  const isEdit = !!engineId;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    status: "ACTIVE",
    code: "",
    contact_name: "",
    contact_phone: "",
    support_email: "",
    notes: "",
    deposit_fee: 0,
    withdrawal_fee: 0,
    percentage_fee: 1.0,
    commission_rate: 0.3,
    max_resellers: 50,
    max_merchants: 100,
    allowed_providers: [],
    // Fee Commission Fields
    depositFeeAmount: 0,
    depositFeeType: "FIXED",
    withdrawalFeeAmount: 0,
    withdrawalFeeType: "FIXED",
    depositCommissionAmount: 0,
    depositCommissionType: "PERCENTAGE",
    withdrawalCommissionAmount: 0,
    withdrawalCommissionType: "PERCENTAGE",
    minDepositFee: "",
    maxDepositFee: "",
    minWithdrawalFee: "",
    maxWithdrawalFee: "",
    minDepositCommission: "",
    maxDepositCommission: "",
    minWithdrawalCommission: "",
    maxWithdrawalCommission: "",
    permissions: {
      can_create_resellers: true,
      can_create_merchants: true,
      can_edit_fees: false,
      can_view_webhook_logs: true,
      can_regenerate_api_keys: true,
    },
    api_key: generateApiKey("eng_pk_"),
    secret_key: generateApiKey("eng_sk_"),
  });

  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [copied, setCopied] = useState(null);

  const { data: existingEngine } = useQuery({
    queryKey: ["engine", engineId],
    queryFn: () => adminService.getEngineById(engineId),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existingEngine) {
      setFormData(prev => ({
        ...prev,
        name: existingEngine.name,
        email: existingEngine.email,
        password: "", // Don't populate password for security
        status: existingEngine.status,
        code: existingEngine.code,
        deposit_fee: existingEngine.depositFee ?? 0,
        commission_rate: existingEngine.depositCommission ?? 0.3,
        percentage_fee: existingEngine.percentageFee ?? 1.0,
        withdrawal_fee: existingEngine.withdrawalFee ?? 0,
        allowed_providers: existingEngine.allowedProviders ?? [],
        max_resellers: existingEngine.maxResellers ?? 50,
        max_merchants: existingEngine.maxMerchants ?? 100,
        contact_name: existingEngine.contactName ?? "",
        contact_phone: existingEngine.contactPhone ?? "",
        support_email: existingEngine.supportEmail ?? "",
        notes: existingEngine.notes ?? "",
        permissions: {
          can_create_merchants: existingEngine.permissions?.can_create_merchants ?? false,
          can_create_resellers: existingEngine.permissions?.can_create_resellers ?? false,
          can_edit_fees: existingEngine.permissions?.can_edit_fees ?? false,
          can_view_webhook_logs: existingEngine.permissions?.can_view_webhook_logs ?? false,
          can_regenerate_api_keys: existingEngine.permissions?.can_regenerate_api_keys ?? false,
        },
        api_key: existingEngine.apiKey ?? prev.api_key,
        secret_key: existingEngine.secretKey ?? prev.secret_key,
      }));
    }
  }, [existingEngine]);


  const createMutation = useMutation({
    mutationFn: (data) => adminService.createEngine(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-engines"]);
      navigate(createPageUrl("AdminEngines"));
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong";
      console.log("Error: ", message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data) => adminService.updateEngine(engineId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-engines"]);
      queryClient.invalidateQueries(["engine", engineId]);
      navigate(createPageUrl("AdminEngines"));
    },
  });

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handlePermissionChange = (key, value) => {
    setFormData(prev => ({ ...prev, permissions: { ...prev.permissions, [key]: value } }));
  };

  const toggleProvider = (code) => {
    const current = formData.allowed_providers || [];
    const updated = current.includes(code) ? current.filter(p => p !== code) : [...current, code];
    handleChange("allowed_providers", updated);
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        // For update, send fields that backend accepts
        const updateData = {
          name: formData.name,
          status: formData.status,
          code: formData.code,
          contactName: formData.contact_name || undefined,
          contactPhone: formData.contact_phone || undefined,
          supportEmail: formData.support_email || undefined,
          notes: formData.notes || undefined,
          // Fees and Limits
          depositFee: formData.deposit_fee,
          withdrawalFee: formData.withdrawal_fee,
          percentageFee: formData.percentage_fee,
          depositCommission: formData.commission_rate,
          maxResellers: formData.max_resellers,
          maxMerchants: formData.max_merchants,
          allowedProviders: formData.allowed_providers,
          // Permissions
          permissions: formData.permissions,
          // API Keys
          apiKey: formData.api_key || undefined,
          secretKey: formData.secret_key || undefined,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await updateMutation.mutateAsync(updateData);
      } else {
        // For create, only send fields that backend accepts (email, password, name)
        // Backend doesn't accept status on create - it defaults to ACTIVE
        if (!formData.email || !formData.password || !formData.name) {
          toast.error(t("pleaseFillAllRequiredFields"));
          setSaving(false);
          return;
        }
        const createData = {
          // General
          email: formData.email,
          password: formData.password,
          name: formData.name,
          code: formData.code,
          contactName: formData.contact_name || undefined,
          contactPhone: formData.contact_phone || undefined,
          supportEmail: formData.support_email || undefined,
          notes: formData.notes || undefined,
          // Fees and Limits
          depositFee: formData.deposit_fee,
          withdrawalFee: formData.withdrawal_fee,
          percentageFee: formData.percentage_fee,
          depositCommission: formData.commission_rate,
          maxResellers: formData.max_resellers,
          maxMerchants: formData.max_merchants,
          allowedProviders: formData.allowed_providers,
          // Permissions
          permissions: formData.permissions,
          // API Keys
          apiKey: formData.api_key,
          secretKey: formData.secret_key,
        };
        await createMutation.mutateAsync(createData);
      }
    } catch (error) {
      // Error is handled by global error handler
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Link to={createPageUrl("AdminEngines")}>
          <Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{isEdit ? t("editEngine") : t("createNewEngine")}</h1>
          <p className="text-slate-500">{isEdit ? t("updateEngineDetails") : t("setUpNewEnginePartner")}</p>
        </div>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="bg-white border border-slate-200 p-1 h-auto w-full overflow-x-auto justify-start whitespace-nowrap">
          <TabsTrigger value="general" className="gap-2 shrink-0 data-[state=active]:bg-sky-100 data-[state=active]:text-sky-700">
            <User className="h-4 w-4" />{t("generalTab")}
          </TabsTrigger>
          <TabsTrigger value="fees" className="gap-2 shrink-0 data-[state=active]:bg-sky-100 data-[state=active]:text-sky-700">
            <Zap className="h-4 w-4" />{t("feesLimitsTab")}
          </TabsTrigger>
          <TabsTrigger value="permissions" className="gap-2 shrink-0 data-[state=active]:bg-sky-100 data-[state=active]:text-sky-700">
            <Lock className="h-4 w-4" />{t("permissionsTab")}
          </TabsTrigger>
          <TabsTrigger value="api" className="gap-2 shrink-0 data-[state=active]:bg-sky-100 data-[state=active]:text-sky-700">
            <Key className="h-4 w-4" />{t("apiAccessTab")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader><CardTitle>{t("generalInformation")}</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("businessNameRequired")}</Label>
                    <Input value={formData.name} onChange={(e) => handleChange("name", e.target.value)} placeholder={t("businessNamePlaceholder")} required />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("engineCode")}</Label>
                    <Input value={formData.code} onChange={(e) => handleChange("code", e.target.value.toUpperCase())} placeholder={t("engineCodePlaceholder")} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("loginEmailRequired")}</Label>
                    <Input 
                      type="email" 
                      value={formData.email} 
                      onChange={(e) => handleChange("email", e.target.value)} 
                      placeholder={t("loginEmailPlaceholder")} 
                      disabled={isEdit} 
                      required 
                      autoComplete="off"
                    />
                    {isEdit && <p className="text-xs text-slate-500">{t("emailCannotBeChanged")}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>{isEdit ? t("newPasswordOptional") : t("passwordRequired")}</Label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative flex-1">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => handleChange("password", e.target.value)}
                          placeholder={isEdit ? t("leaveBlankToKeepCurrent") : t("enterPassword")}
                          required={!isEdit}
                          className="pr-10"
                          autoComplete="new-password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3 text-slate-400 hover:text-slate-600 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-10 px-3 flex items-center gap-2 group border border-blue-100 hover:border-blue-600 hover:bg-blue-600 hover:text-white text-blue-600 font-medium transition-all duration-300 rounded-lg shadow-sm w-full sm:w-auto flex-shrink-0"
                        onClick={() => {
                          const newPass = generatePassword();
                          handleChange("password", newPass);
                          navigator.clipboard.writeText(newPass);
                        }}
                      >
                        <RefreshCw className="h-4 w-4 group-hover:rotate-180 transition-transform duration-500" />
                        <span className="text-xs uppercase tracking-wide">{t("generatePassword")}</span>
                      </Button>
                    </div>
                  </div>
                </div>
                {isEdit && (
                  <div className="space-y-2">
                    <Label>{t("status")}</Label>
                    <Select value={formData.status} onValueChange={(v) => handleChange("status", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACTIVE">{t("active")}</SelectItem>
                        <SelectItem value="INACTIVE">{t("inactive")}</SelectItem>
                        <SelectItem value="SUSPENDED">{t("suspended")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>{t("contactName")}</Label><Input value={formData.contact_name} onChange={(e) => handleChange("contact_name", e.target.value)} /></div>
                  <div className="space-y-2"><Label>{t("contactPhone")}</Label><Input value={formData.contact_phone} onChange={(e) => handleChange("contact_phone", e.target.value)} /></div>
                </div>
                <div className="space-y-2"><Label>{t("supportEmail")}</Label><Input type="email" value={formData.support_email} onChange={(e) => handleChange("support_email", e.target.value)} /></div>
                <div className="space-y-2"><Label>{t("notes")}</Label><Textarea value={formData.notes} onChange={(e) => handleChange("notes", e.target.value)} rows={3} /></div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="fees">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <FeeCommissionSection
              feeData={{
                depositFeeAmount: formData.depositFeeAmount,
                depositFeeType: formData.depositFeeType,
                withdrawalFeeAmount: formData.withdrawalFeeAmount,
                withdrawalFeeType: formData.withdrawalFeeType,
                depositCommissionAmount: formData.depositCommissionAmount,
                depositCommissionType: formData.depositCommissionType,
                withdrawalCommissionAmount: formData.withdrawalCommissionAmount,
                withdrawalCommissionType: formData.withdrawalCommissionType,
                minDepositFee: formData.minDepositFee,
                maxDepositFee: formData.maxDepositFee,
                minWithdrawalFee: formData.minWithdrawalFee,
                maxWithdrawalFee: formData.maxWithdrawalFee,
                minDepositCommission: formData.minDepositCommission,
                maxDepositCommission: formData.maxDepositCommission,
                minWithdrawalCommission: formData.minWithdrawalCommission,
                maxWithdrawalCommission: formData.maxWithdrawalCommission,
              }}
              onChange={(newFeeData) => {
                setFormData((prev) => ({ ...prev, ...newFeeData }));
              }}
              entityType="ENGINE"
              entityId={engineId}
              showConstraints={true}
              description={t("configureDepositAndWithdrawalFeesForThisEngine")}
            />

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>{t("additionalSettings")}</CardTitle>
                <CardDescription>{t("operationalLimitsAndPaymentMethods")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{t("maxResellers")}</Label>
                    <Input type="number" value={formData.max_resellers} onChange={(e) => handleChange("max_resellers", parseInt(e.target.value))} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("maxMerchants")}</Label>
                    <Input type="number" value={formData.max_merchants} onChange={(e) => handleChange("max_merchants", parseInt(e.target.value))} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("commissionRatePercent")}</Label>
                    <Input type="number" step="0.1" value={formData.commission_rate} onChange={(e) => handleChange("commission_rate", parseFloat(e.target.value))} />
                  </div>
                </div>
                <div className="space-y-3">
                  <Label>{t("allowedPaymentProviders")}</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {providers.map(p => (
                      <div key={p.code} className="flex items-center gap-2 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer" onClick={() => toggleProvider(p.code)}>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${formData.allowed_providers?.includes(p.code) ? "bg-sky-500 border-sky-500" : "border-slate-300"}`}>
                          {formData.allowed_providers?.includes(p.code) && <CheckCircle2 className="h-3 w-3 text-white" />}
                        </div>
                        <div><p className="text-sm font-medium">{p.name}</p><p className="text-xs text-slate-500">{p.code}</p></div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="permissions">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader><CardTitle>{t("permissionsTab")}</CardTitle><CardDescription>{t("controlEngineActions")}</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                {Object.entries({
                  can_create_resellers: { label: t("canCreateResellers"), desc: t("allowCreatingNewResellerAccounts") },
                  can_create_merchants: { label: t("canCreateMerchantsDirect"), desc: t("allowCreatingMerchantAccountsDirectly") },
                  can_edit_fees: { label: t("canEditFeeStructure"), desc: t("allowModifyingFeeSettings") },
                  can_view_webhook_logs: { label: t("canViewWebhookLogs"), desc: t("accessToRawWebhookData") },
                  can_regenerate_api_keys: { label: t("canRegenerateApiKeys"), desc: t("allowRegeneratingApiCredentials") }
                }).map(([key, { label, desc }]) => (
                  <div key={key} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                    <div><p className="font-medium">{label}</p><p className="text-sm text-slate-500">{desc}</p></div>
                    <Switch checked={formData.permissions?.[key] || false} onCheckedChange={(v) => handlePermissionChange(key, v)} />
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="api">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader><CardTitle>{t("apiCredentials")}</CardTitle><CardDescription>{t("shareCredentialsSecurely")}</CardDescription></CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex gap-3"><Shield className="h-5 w-5 text-amber-600" /><p className="text-sm text-amber-700">{t("keepCredentialsSecure")}</p></div>
                </div>
                <div className="space-y-2">
                  <Label>{t("apiKey")}</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 p-3 bg-slate-900 rounded-lg font-mono text-sm text-white overflow-x-auto">{showApiKey ? formData.api_key : "••••••••••••••••••••••••"}</div>
                    <Button variant="outline" size="icon" onClick={() => setShowApiKey(!showApiKey)}>{showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
                    <Button variant="outline" size="icon" onClick={() => copyToClipboard(formData.api_key, "api")}>{copied === "api" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}</Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t("secretKey")}</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 p-3 bg-slate-900 rounded-lg font-mono text-sm text-white overflow-x-auto">{showSecretKey ? formData.secret_key : "••••••••••••••••••••••••"}</div>
                    <Button variant="outline" size="icon" onClick={() => setShowSecretKey(!showSecretKey)}>{showSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
                    <Button variant="outline" size="icon" onClick={() => copyToClipboard(formData.secret_key, "secret")}>{copied === "secret" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>

      <div className="flex items-center justify-end gap-4 pt-4 border-t">
        <Link to={createPageUrl("AdminEngines")}><Button variant="outline">{t("cancel")}</Button></Link>
        <Button
          onClick={handleSubmit}
          disabled={saving || !formData.name || !formData.email || (!isEdit && !formData.password)}
          className="gap-2"
        >
          <Save className="h-4 w-4" />
          {saving ? t("saving") : isEdit ? t("updateEngine") : t("createEngine")}
        </Button>
      </div>
    </div>
  );
}